import { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';

import { notificationsService } from '../app/services/notifications.service';
import { userManagementService } from '../app/services/user-management.service';

/**
 * Event name that components can dispatch on `window` to trigger a refresh of
 * the unread notifications counter (e.g. after marking notifications as read).
 */
export const NOTIFICATIONS_UPDATED_EVENT = 'notifications:updated';

export const dispatchNotificationsUpdated = (unreadDelta?: number) => {
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail: { unreadDelta } }));
};

export function useUnreadNotificationsCount() {
  const [count, setCount] = useState<number>(0);
  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();

  const refresh = useCallback(async () => {
    try {
      const organisatie = userManagementService.getStoredOrganisatie();
      if (!organisatie || !organisatie.id) {
        setCount(0);
        return;
      }
      const result = await notificationsService.getUnreadCount();
      setCount(result?.unreadCount ?? 0);
    }
    catch {
      // Stil falen: het tellertje is niet kritisch voor de werking van de app
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setCount(0);
      return;
    }

    refresh();
  }, [isAuthenticated, location.pathname, refresh]);

  useEffect(() => {
    if (!isAuthenticated) {
      setCount(0);
      return;
    }

    let reconcileTimer: ReturnType<typeof setTimeout> | undefined;

    const handler = (event: Event) => {
      const delta = (event as CustomEvent<{ unreadDelta?: number }>).detail?.unreadDelta;
      if (typeof delta === 'number' && delta !== 0) {
        // Direct optimistisch bijwerken voor een responsieve teller...
        setCount(prev => Math.max(0, prev + delta));
        // ...en kort daarna reconciliëren met de server (read-model kan iets achterlopen)
        if (reconcileTimer) clearTimeout(reconcileTimer);
        reconcileTimer = setTimeout(refresh, 2000);
      }
      else {
        refresh();
      }
    };

    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
    return () => {
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handler);
      if (reconcileTimer) clearTimeout(reconcileTimer);
    };
  }, [isAuthenticated, refresh]);

  return { count, refresh };
}
